import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { useToast } from '../shell/toast';
import { ApiFailure } from '@/lib/result';
import type { InviteForm } from './workspace-form';

const INVITES_KEY = ['workspace', 'invites'] as const;

const MESSAGES: Record<number, string> = {
  400: 'Enter a valid email address.',
  409: 'They are already in this workspace.',
};

/** The current workspace's pending invitations, sending one, and revoking one. */
export function useInvites(onSent: () => void) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const pending = useQuery({
    queryKey: INVITES_KEY,
    queryFn: async () => {
      const result = await api.workspaces.invites();
      if (result.status !== 200) throw new ApiFailure(result.status, undefined);
      return result.body.invites;
    },
  });
  const invite = useMutation({
    mutationFn: async (values: InviteForm) => {
      const result = await api.workspaces.invite({ body: values });
      if (result.status !== 201) {
        throw new Error(MESSAGES[result.status] ?? 'The invitation was not sent.');
      }
      return result.body.invite;
    },
    onSuccess: async (sent) => {
      onSent();
      toast(`Invitation sent to ${sent.email}`);
      await queryClient.invalidateQueries({ queryKey: INVITES_KEY });
    },
  });
  const revoke = useMutation({
    mutationFn: async (id: string) => {
      await api.workspaces.revokeInvite({ params: { inviteId: id } });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INVITES_KEY }),
  });
  return { pending, invite, revoke };
}
